

import { useState, useEffect, useMemo, Suspense } from "react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { Plus, Search, Pencil, Trash2, MapPin } from "lucide-react";

interface Venue {
  id: number;
  name: string;
  location: string;
  capacity: number;
  description?: string;
  is_active: boolean;
}

// Reusable Action Button Component
function ActionButton({
  onClick,
  icon: Icon,
  label,
  variant = "default",
}: {
  onClick: () => void;
  icon: any;
  label: string;
  variant?: "default" | "primary" | "danger";
}) {
  const colors = {
    default: "text-stone-600 hover:bg-stone-100",
    primary: "text-amber-600 hover:bg-amber-50",
    danger: "text-red-600 hover:bg-red-50",
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Venue Form Component
function VenueForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<Venue>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || "");
  const [location, setLocation] = useState(initialData?.location || "");
  const [capacity, setCapacity] = useState(initialData?.capacity || 0);
  const [description, setDescription] = useState(initialData?.description || "");
  const [is_active, setIsActive] = useState(initialData?.is_active ?? true);

  // Reset form when initialData changes (for new entries)
  useEffect(() => {
    if (!initialData?.id) {
      setName("");
      setLocation("");
      setCapacity(0);
      setDescription("");
      setIsActive(true);
    } else {
      setName(initialData.name || "");
      setLocation(initialData.location || "");
      setCapacity(initialData.capacity || 0);
      setDescription(initialData.description || "");
      setIsActive(initialData.is_active ?? true);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, location, capacity, description, is_active });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Venue Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Grand Ballroom"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Location</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g., Downtown, New Delhi"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Capacity</label>
        <input
          type="number"
          value={capacity}
          onChange={(e) => setCapacity(parseInt(e.target.value) || 0)}
          placeholder="0"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add venue details..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_active"
          checked={is_active}
          onChange={(e) => setIsActive(e.target.checked)}
          className="rounded border-stone-300"
        />
        <label htmlFor="is_active" className="text-sm text-stone-600">
          Active
        </label>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Create"} Venue
        </button>
      </div>
    </form>
  );
}

function VenuesPageContent() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingVenue, setDeletingVenue] = useState<Venue | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.venues.list();
      // Handle paginated response: { data: { data: [...], current_page: 1, ... } }
      const venuesData = response.data?.data?.data || response.data?.data || response.data || [];
      setVenues(venuesData);
    } catch (error) {
      showToast("Failed to load venues", "error");
      console.error("Error loading venues:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVenues = useMemo(() => {
    if (!searchQuery) return venues;
    const q = searchQuery.toLowerCase();
    return venues.filter((v) => 
      v.name.toLowerCase().includes(q) ||
      v.location?.toLowerCase().includes(q)
    );
  }, [venues, searchQuery]);

  const handleDelete = async () => {
    if (!deletingVenue) return;
    setDeleteLoading(true);
    try {
      await mastersApi.venues.delete(deletingVenue.id);
      showToast("Venue deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingVenue(null);
      loadData(); // Reload data to ensure consistency
    } catch (error: any) {
      const errorMessage = error.message || "Failed to delete venue";
      showToast(errorMessage, "error");
      setDeleteModalOpen(false);
      setDeletingVenue(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingVenue) {
        // Update existing
        await mastersApi.venues.update(editingVenue.id, data);
        showToast("Venue updated successfully", "success");
      } else {
        // Create new
        await mastersApi.venues.create(data);
        showToast("Venue created successfully", "success");
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingVenue(null);
      loadData(); // Reload to get fresh data
    } catch (error) {
      showToast("Failed to save venue", "error");
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Venues</h1>
        <p className="text-sm text-stone-500">Manage event venues and locations</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search venues..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        {!isStaff && (
          <button
            onClick={() => { setEditingVenue(null); setIsModalOpen(true); }}
            className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Venue
          </button>
        )}
      </div>

      {/* Content */}
      {filteredVenues.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No venues found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Location</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Capacity</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredVenues.map((venue) => (
                  <tr key={venue.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{venue.name}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{venue.location || '-'}</td>
                    <td className="px-4 py-3 text-center text-sm">{venue.capacity || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-lg font-medium ${
                          venue.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {venue.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!isStaff && (
                        <div className="flex justify-end gap-1">
                          <ActionButton 
                            onClick={() => { setEditingVenue(venue); setIsModalOpen(true); }} 
                            icon={Pencil} 
                            label="Edit" 
                          />
                          <ActionButton 
                            onClick={() => { setDeletingVenue(venue); setDeleteModalOpen(true); }} 
                            icon={Trash2} 
                            label="Delete" 
                            variant="danger" 
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingVenue(null); }}
        title={editingVenue ? "Edit Venue" : "Add New Venue"}
        size="md"
      >
        <VenueForm
          initialData={editingVenue || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingVenue(null); }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingVenue(null); }}
        onConfirm={handleDelete}
        title="Delete Venue"
        message="Are you sure you want to delete this venue? This action cannot be undone."
        itemName={deletingVenue?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    </DashboardLayout>
  );
}

export default function VenuesPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <VenuesPageContent />
    </Suspense>
  );
}
